"use client";

import type { FC } from "react";
import {
  FormRow,
  NumberField,
  Select,
  Spacing,
  Grid,
  Message,
} from "@huub-web/ui";
import {
  InputConfiguration,
  FormInputDisplayConfiguration,
} from "@huub-web/ui/services/inputs";
import { useTranslation } from "react-i18next";

import { useMaximumMortgageWizard } from "../../../hooks/useMaximumMortgageWizard";
import type { MortgageAge } from "../../../types/MaximumMortgageWizard";
import { OptionalFormRow } from "./OptionalFormRow";
import { BaseSlide, type BaseSlideProps } from "./BaseSlide";
import { twoColumnGridStyle } from "./BaseSlide.css";

type IncomeSlideProps = BaseSlideProps;

const AGE_RANGE_OPTIONS = [
  { value: "18-34", label: "18 - 34" },
  { value: "35-56", label: "35 - 56" },
  { value: "57-66", label: "57 - 66" },
  { value: "67+", label: "67+" },
];

/**
 * Income slide component for the mortgage wizard
 * Handles income, age, and optional partner information
 *
 * ⚠️ When adding/removing conditional fields, update filterMaximumMortgageWizardValues.ts
 */
export const IncomeSlide: FC<IncomeSlideProps> = () => {
  const { t } = useTranslation();
  const { state, updateFormValues } = useMaximumMortgageWizard();
  const { formValues } = state;

  return (
    <BaseSlide
      slideKey="income"
      title={t("maxMortgageWizard.income.title")}
      description={t("maxMortgageWizard.income.description")}
    >
      <Grid variant="none" className={twoColumnGridStyle}>
        <FormRow id="wizard__income" data-field-key="income">
          <NumberField
            {...InputConfiguration.money.props}
            leading={FormInputDisplayConfiguration.money.leading}
            value={
              formValues.income && formValues.income !== ""
                ? Number(formValues.income)
                : undefined
            }
            name={t("maxMortgageWizard.income.grossAnnualSalaryName")}
            onChange={(value) =>
              updateFormValues({ income: value?.toString() ?? "" })
            }
            max={999999}
            min={0}
            validation={["required"]}
            label={t("maxMortgageWizard.income.grossAnnualSalaryLabel")}
          />
        </FormRow>

        <FormRow id="wizard__age" data-field-key="age">
          <Select
            value={formValues.age ?? ""}
            options={AGE_RANGE_OPTIONS}
            name={t("maxMortgageWizard.income.ageName")}
            onChange={(value) =>
              updateFormValues({ age: value as MortgageAge })
            }
            validation={["required"]}
            label={t("maxMortgageWizard.income.ageLabel")}
          />
        </FormRow>
      </Grid>

      {formValues.age && ["57-66", "67+"].includes(formValues.age) && (
        <Message>{t("maxMortgageWizard.income.pensionMessage")}</Message>
      )}

      <Spacing />

      <OptionalFormRow
        id="wizard__partner"
        data-field-key="hasPartner"
        label={t("maxMortgageWizard.income.partnerLabel")}
        description={t("maxMortgageWizard.income.partnerDescription")}
        checked={formValues.hasPartner ?? false}
        onChange={(value) =>
          updateFormValues({
            hasPartner: value,
            ...(value ? {} : { partnerIncome: "", partnerAge: undefined }),
          })
        }
      >
        <Spacing />
        <Grid variant="none" className={twoColumnGridStyle}>
          <FormRow id="wizard__partner_income" data-field-key="partnerIncome">
            <NumberField
              {...InputConfiguration.money.props}
              leading={FormInputDisplayConfiguration.money.leading}
              value={
                formValues.partnerIncome && formValues.partnerIncome !== ""
                  ? Number(formValues.partnerIncome)
                  : undefined
              }
              onChange={(value) =>
                updateFormValues({ partnerIncome: value?.toString() ?? "" })
              }
              max={999999}
              min={0}
              validation={["required"]}
              name={t("maxMortgageWizard.income.partnerGrossAnnualSalaryName")}
              label={t(
                "maxMortgageWizard.income.partnerGrossAnnualSalaryLabel",
              )}
            />
          </FormRow>

          <FormRow id="wizard__partner_age" data-field-key="partnerAge">
            <Select
              value={formValues.partnerAge ?? ""}
              options={AGE_RANGE_OPTIONS}
              name={t("maxMortgageWizard.income.partnerAgeName")}
              onChange={(value) =>
                updateFormValues({ partnerAge: value as MortgageAge })
              }
              validation={["required"]}
              label={t("maxMortgageWizard.income.partnerAgeLabel")}
            />
          </FormRow>
        </Grid>

        {formValues.partnerAge &&
          ["57-66", "67+"].includes(formValues.partnerAge) &&
          !(formValues.age && ["57-66", "67+"].includes(formValues.age)) && (
            <Message>
              {t("maxMortgageWizard.income.partnerPensionMessage")}
            </Message>
          )}
      </OptionalFormRow>
    </BaseSlide>
  );
};
